import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { ChatInputElementId } from '@renderer/chat/components'

import { useSidepanelRepositioning } from '../../atoms'
import { type QuickTourTarget } from './build-tour-steps'

/** Viewport Y the card anchors to for each target. */
export type TourTargetCenters = Partial<Record<QuickTourTarget, number>>

/**
 * How far into a target the anchor can sit. Short sections (the designs' own
 * one-row examples) anchor at their center; a section stuffed with real content
 * anchors just below its header instead of dragging the card down to the middle
 * of a 1000px list.
 */
const MAX_ANCHOR_OFFSET_PX = 60

export const tourTargetElement = (target: QuickTourTarget): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-tour-section="${target}"]`)

/**
 * What the card actually points at. The chat step is the exception: its
 * `data-tour-section` is the whole footer shell (a tall, mostly transparent
 * region), so anchoring to its top would leave the card floating mid-screen
 * instead of beside the input the step is about.
 */
const anchorElement = (target: QuickTourTarget): HTMLElement | null =>
  target === 'chat'
    ? (document.getElementById(ChatInputElementId) ?? tourTargetElement(target))
    : tourTargetElement(target)

const measureCenters = (targets: Array<QuickTourTarget>): TourTargetCenters => {
  const centers: TourTargetCenters = {}
  for (const target of targets) {
    const element = anchorElement(target)
    if (!element) continue
    const rect = element.getBoundingClientRect()
    centers[target] = rect.top + Math.min(rect.height / 2, MAX_ANCHOR_OFFSET_PX)
  }
  return centers
}

const sameCenters = (a: TourTargetCenters, b: TourTargetCenters): boolean => {
  const keys = Object.keys(b) as Array<QuickTourTarget>
  if (Object.keys(a).length !== keys.length) return false
  return keys.every((key) => a[key] === b[key])
}

/**
 * Tracks where each tour target sits on screen. Targets are found by
 * `data-tour-section`, so a user's custom section order resolves correctly
 * without the tour knowing anything about the order.
 */
export function useTourTargetPositions(isVisible: boolean, targets: Array<QuickTourTarget>): TourTargetCenters {
  const [centers, setCenters] = useState<TourTargetCenters>({})
  const isRepositioning = useSidepanelRepositioning()
  const targetKey = targets.join(',')
  const measured = useMemo(() => targetKey.split(',') as Array<QuickTourTarget>, [targetKey])

  const update = useCallback(() => {
    const next = measureCenters(measured)
    setCenters((prev) => (sameCenters(prev, next) ? prev : next))
  }, [measured])

  useLayoutEffect(() => {
    if (!isVisible) {
      setCenters({})
      return
    }

    update()
    const raf = requestAnimationFrame(update)

    window.addEventListener('resize', update)
    // Capture scroll from any ancestor scroll container (e.g. Sidepanel.Body).
    window.addEventListener('scroll', update, true)

    // Sections grow as their async content lands (meetings, drafted actions),
    // which moves everything below them — re-measure instead of going stale.
    // The panel root is observed too. A resize commit changes the panel's box
    // without changing any anchor's own size, so the anchors alone miss it.
    const observer = new ResizeObserver(update)
    for (const target of measured) {
      const element = anchorElement(target)
      if (element) observer.observe(element)
    }
    const panelRoot = document.querySelector<HTMLElement>('[data-sidepanel-root]')
    if (panelRoot) observer.observe(panelRoot)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      observer.disconnect()
    }
  }, [isVisible, measured, update])

  // A reposition drag translates the panel root outside React: no resize, no
  // scroll, no size change for the ResizeObserver above to see. The flag bounds
  // the loop to the gesture.
  useLayoutEffect(() => {
    if (!isVisible || !isRepositioning) return

    let frame = requestAnimationFrame(function track() {
      update()
      frame = requestAnimationFrame(track)
    })
    return () => cancelAnimationFrame(frame)
  }, [isVisible, isRepositioning, update])

  return centers
}
